import type { FC, PointerEvent, ReactNode } from 'react'
import type { Project } from '@/types'
import type { MotionValue } from 'motion/react'

import { useMemo, useState } from 'react'
import { ArrowUpRight, Plus } from 'lucide-react'

import {
    AnimatePresence,
    LayoutGroup,
    motion,
    useMotionValue,
    useSpring
} from 'motion/react'

import { cn } from '@/lib/utils'
import { displayUrl, pad, readableOn } from '@/lib/format'
import { useFinePointer } from '@/hooks/use-media-query'
import { Marquee } from '@/components/landing/marquee'
import { projectsIntro } from '@/constants/content'

interface ProjectsProps {
    projects: Array<Project>
}

interface PreviewProps {
    projects: Array<Project>
    activeId: string | null
    x: MotionValue<number>
    y: MotionValue<number>
}

// Floating card that follows the cursor over the index and shows the hovered project's mark.
const ProjectPreview: FC<PreviewProps> = ({ projects, activeId, x, y }): ReactNode => {
    return (
        <motion.div
            aria-hidden='true'
            style={{ x, y }}
            className='pointer-events-none fixed left-0 top-0 z-40'
        >
            <motion.div
                initial={false}
                animate={{ opacity: activeId ? 1 : 0, scale: activeId ? 1 : 0.86, rotate: activeId ? -2 : 0 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className='relative h-[18vw] w-[25vw] overflow-hidden shadow-[0_30px_80px_-30px_rgb(20_20_21/0.45)]'
            >
                {projects.map((project) => {
                    const isActive = project.id === activeId

                    return (
                        <div
                            key={project.id}
                            style={{ backgroundColor: project.ngjyra, color: readableOn(project.ngjyra) }}
                            className={cn(
                                'absolute inset-0 flex flex-col items-center justify-center gap-4 p-[2vw] transition-[clip-path] duration-700 ease-out-expo',
                                isActive ? 'z-10 [clip-path:inset(0%_0%_0%_0%)]' : 'z-0 [clip-path:inset(100%_0%_0%_0%)]'
                            )}
                        >
                            {project.logo && (
                                // eslint-disable-next-line @next/next/no-img-element
                                <img
                                    src={project.logo}
                                    alt=''
                                    decoding='async'
                                    className='max-h-[55%] max-w-[72%] object-contain'
                                />
                            )}

                            {project.faqja && (
                                <span className='absolute bottom-[1vw] left-[1.2vw] text-[0.7rem] font-semibold uppercase tracking-[0.16em] opacity-70'>
                                    {displayUrl(project.faqja)}
                                </span>
                            )}
                        </div>
                    )
                })}
            </motion.div>
        </motion.div>
    )
}

interface ProjectRowProps {
    project: Project
    number: number
    open: boolean
    onToggle: () => void
    onHover: (id: string | null) => void
}

const ProjectRow: FC<ProjectRowProps> = ({ project, number, open, onToggle, onHover }): ReactNode => {
    const panelId = `projekt-${project.slug}`
    const buttonId = `projekt-${project.slug}-titulli`

    return (
        <motion.li
            layout='position'
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12, transition: { duration: 0.25 } }}
            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
            className='border-b border-tone-line'
        >
            <h3>
                <button
                    id={buttonId}
                    type='button'
                    aria-expanded={open}
                    aria-controls={panelId}
                    onClick={onToggle}
                    onPointerEnter={() => onHover(project.id)}
                    onPointerLeave={() => onHover(null)}
                    className='group grid w-full grid-cols-12 items-center gap-x-4 py-5 text-left md:gap-x-5 md:py-7'
                >
                    <span className='col-span-2 font-heading text-xs font-semibold tabular-nums text-tone-muted md:col-span-1 md:text-sm'>
                        {pad(number)}
                    </span>

                    <span className='col-span-9 md:col-span-6'>
                        <span
                            className='inline-block font-heading text-[2rem] leading-[0.95] tracking-[-0.035em] transition-[font-variation-settings,translate,color] duration-700 ease-out-expo [font-variation-settings:"wdth"_92,"wght"_640] group-hover:translate-x-2 group-hover:text-tone-accent group-hover:[font-variation-settings:"wdth"_118,"wght"_760] sm:text-5xl lg:text-[4.4vw]'
                            data-mdlfy-cms={`projektet:${project.id}:emri`}
                        >
                            {project.emri}
                        </span>
                    </span>

                    <span className='hidden text-sm text-tone-muted md:col-span-3 md:flex md:flex-col md:gap-0.5'>
                        <span className='font-semibold text-tone-fg' data-mdlfy-cms={`projektet:${project.id}:fusha`}>
                            {project.fusha}
                        </span>

                        <span data-mdlfy-cms={`projektet:${project.id}:etiketa`}>
                            {project.etiketa}
                        </span>
                    </span>

                    <span className='col-span-1 flex items-center justify-end gap-4 md:col-span-2'>
                        {project.faqja && (
                            <span className='hidden truncate text-sm text-tone-muted xl:inline'>
                                {displayUrl(project.faqja)}
                            </span>
                        )}

                        <span
                            aria-hidden='true'
                            className={cn(
                                'grid size-9 shrink-0 place-items-center border border-tone-line transition-[rotate,background-color,color,border-color] duration-500 ease-out-expo group-hover:border-tone-accent md:size-11',
                                open && 'rotate-45 border-tone-accent bg-tone-accent text-tone-accent-fg'
                            )}
                        >
                            <Plus className='size-4' />
                        </span>
                    </span>
                </button>
            </h3>

            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        id={panelId}
                        role='region'
                        aria-labelledby={buttonId}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
                        className='overflow-hidden'
                    >
                        <div className='grid grid-cols-12 gap-x-5 gap-y-8 pb-12 pt-2 md:pb-16'>
                            <p className='col-span-12 flex flex-wrap gap-x-2 text-sm text-tone-muted md:hidden'>
                                <span className='font-semibold text-tone-fg' data-mdlfy-cms={`projektet:${project.id}:fusha`}>
                                    {project.fusha}
                                </span>

                                <span aria-hidden='true'>·</span>

                                <span data-mdlfy-cms={`projektet:${project.id}:etiketa`}>
                                    {project.etiketa}
                                </span>
                            </p>

                            <p className='col-span-12 font-serif text-3xl italic leading-[1.1] md:col-span-6 md:col-start-2 lg:text-[2.6vw]'>
                                <span data-mdlfy-cms={`projektet:${project.id}:slogani`}>{project.slogani}</span>
                            </p>

                            <div className='col-span-12 flex flex-col gap-6 md:col-span-4 md:col-start-8'>
                                <p
                                    className='whitespace-pre-line text-base leading-relaxed text-tone-muted md:text-lg'
                                    data-mdlfy-cms={`projektet:${project.id}:pershkrimi`}
                                >
                                    {project.pershkrimi}
                                </p>

                                {project.faqja && (
                                    <a
                                        href={project.faqja}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className='group inline-flex w-fit items-center gap-2 border-b border-tone-fg pb-1 text-sm font-semibold transition-colors hover:border-tone-accent hover:text-tone-accent'
                                    >
                                        {projectsIntro.visit}
                                        <span className='font-normal text-tone-muted'>{displayUrl(project.faqja)}</span>
                                        <ArrowUpRight className='size-4 transition-transform duration-500 ease-out-expo group-hover:rotate-45' />
                                    </a>
                                )}
                            </div>

                            {project.logo && (
                                <div
                                    style={{ backgroundColor: project.ngjyra }}
                                    className='col-span-12 grid aspect-[16/9] place-items-center p-8 md:col-span-6 md:col-start-2 lg:col-span-4 lg:col-start-2'
                                >
                                    {/* eslint-disable-next-line @next/next/no-img-element */}
                                    <img
                                        src={project.logo}
                                        alt={project.logo_alt}
                                        loading='lazy'
                                        decoding='async'
                                        className='max-h-[60%] max-w-[70%] object-contain'
                                    />
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.li>
    )
}

// Editorial index of every project, filterable by field, with a cursor preview on desktop.
export const Projects: FC<ProjectsProps> = ({ projects = [] }): ReactNode => {
    const finePointer = useFinePointer()
    const [filter, setFilter] = useState<string | null>(null)
    const [openId, setOpenId] = useState<string | null>(null)
    const [hoverId, setHoverId] = useState<string | null>(null)
    const [armed, setArmed] = useState(false)

    const pointerX = useMotionValue(0)
    const pointerY = useMotionValue(0)
    const previewX = useSpring(pointerX, { stiffness: 220, damping: 28, mass: 0.5 })
    const previewY = useSpring(pointerY, { stiffness: 220, damping: 28, mass: 0.5 })

    const fields = useMemo(() => {
        const counts = new Map<string, number>()

        projects.forEach((project) => {
            if (project.fusha) counts.set(project.fusha, (counts.get(project.fusha) ?? 0) + 1)
        })

        return Array.from(counts.entries())
    }, [projects])

    const numbers = useMemo(() => new Map(projects.map((project, index) => [project.id, index + 1])), [projects])
    const visible = filter ? projects.filter((project) => project.fusha === filter) : projects

    // The card sits beside the cursor and flips to the other side near the right edge.
    const onPointerMove = (event: PointerEvent<HTMLUListElement>) => {
        if (!finePointer) return

        const width = window.innerWidth * 0.25
        const height = window.innerWidth * 0.18
        const gap = window.innerWidth * 0.03
        const flip = event.clientX > window.innerWidth * 0.6
        const nextX = flip ? event.clientX - width - gap : event.clientX + gap
        const nextY = event.clientY - height / 2

        if (!armed) {
            pointerX.jump(nextX)
            pointerY.jump(nextY)
            setArmed(true)
        }

        pointerX.set(nextX)
        pointerY.set(nextY)
    }

    const filterButton = (value: string | null, label: string, count: number) => {
        const active = filter === value

        return (
            <button
                key={label}
                type='button'
                aria-pressed={active}
                onClick={() => {
                    setFilter(value)
                    setOpenId(null)
                }}
                className={cn(
                    'relative flex items-baseline gap-1.5 py-2 text-sm font-semibold transition-colors md:text-base',
                    active ? 'text-tone-fg' : 'text-tone-muted hover:text-tone-fg'
                )}
            >
                {label}

                <sup className='font-heading text-[0.65rem] tabular-nums'>{pad(count)}</sup>

                {active && (
                    <motion.span
                        layoutId='projects-filter'
                        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                        className='absolute inset-x-0 bottom-0 h-[2px] bg-tone-accent'
                    />
                )}
            </button>
        )
    }

    return (
        <section id='projektet' data-tone='paper' aria-labelledby='projektet-titulli' className='relative pt-24 md:pt-32'>
            <Marquee projects={projects} />

            <div className='px-5 pb-24 pt-20 md:px-8 md:pb-36 md:pt-28 lg:px-12'>
                <div className='grid grid-cols-12 gap-x-5 gap-y-8'>
                    <p className='col-span-12 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em] text-tone-muted md:col-span-4'>
                        <span className='font-heading tabular-nums text-tone-accent'>05</span>
                        <span aria-hidden='true' className='h-px w-8 bg-tone-line' />
                        {projectsIntro.label}
                    </p>

                    <div className='col-span-12 md:col-span-4 md:col-start-9'>
                        <p className='text-lg leading-relaxed text-tone-muted'>
                            {projectsIntro.text}
                        </p>
                    </div>

                    <h2
                        id='projektet-titulli'
                        className='col-span-12 flex flex-wrap items-end gap-x-[2vw] font-heading text-[18vw] font-black leading-[0.82] tracking-[-0.06em] [font-stretch:92%] md:text-[15vw]'
                    >
                        <span>{projectsIntro.title}</span>

                        <span className='pb-[0.08em] font-serif text-[0.34em] font-normal italic tracking-normal text-tone-accent'>
                            {projectsIntro.titleAccent}
                            <sup className='ml-2 font-heading text-[0.4em] font-semibold not-italic tabular-nums'>
                                {pad(projects.length)}
                            </sup>
                        </span>
                    </h2>
                </div>

                {projects.length > 0 ? (
                    <LayoutGroup>
                        <div role='group' aria-label="Filtro projektet sipas fushës" className='mt-14 flex flex-wrap gap-x-7 gap-y-2 border-b border-tone-line md:mt-20'>
                            {filterButton(null, projectsIntro.all, projects.length)}
                            {fields.map(([field, count]) => filterButton(field, field, count))}
                        </div>

                        <ul className='project-index' onPointerMove={onPointerMove} onPointerLeave={() => setHoverId(null)}>
                            <AnimatePresence initial={false}>
                                {visible.map((project) => (
                                    <ProjectRow
                                        key={project.id}
                                        project={project}
                                        number={numbers.get(project.id) ?? 0}
                                        open={openId === project.id}
                                        onToggle={() => setOpenId((current) => (current === project.id ? null : project.id))}
                                        onHover={setHoverId}
                                    />
                                ))}
                            </AnimatePresence>
                        </ul>
                    </LayoutGroup>
                ) : (
                    <p className='mt-14 text-lg text-tone-muted'>{projectsIntro.empty}</p>
                )}
            </div>

            {finePointer && armed && (
                <ProjectPreview
                    projects={projects}
                    activeId={hoverId && hoverId !== openId ? hoverId : null}
                    x={previewX}
                    y={previewY}
                />
            )}
        </section>
    )
}
